if ('customElements' in window && !customElements.get('visit-carousel')) {
  customElements.define(
    'visit-carousel',
    class VisitCarousel extends HTMLElement {
      connectedCallback() {
        if (this.events) return;

        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        if (this.slides.length < 2) return;

        this.index = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (this.index < 0) this.index = 0;

        const interval = Number.parseInt(this.dataset.interval, 10);
        this.interval = Number.isFinite(interval) && interval >= 1000 ? interval : 4000;

        // Someone who has asked for less motion gets the dots and no timer,
        // rather than losing the other photos entirely.
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.events = new AbortController();
        const { signal } = this.events;

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.show(i, { manual: true }), { signal });
        });

        this.querySelector('[data-carousel-prev]')
          ?.addEventListener('click', () => this.show(this.index - 1, { manual: true }), { signal });
        this.querySelector('[data-carousel-next]')
          ?.addEventListener('click', () => this.show(this.index + 1, { manual: true }), { signal });

        // Pause while the customer is looking at it on purpose, and while the
        // tab is in the background, where the timer would only burn battery.
        this.addEventListener('pointerenter', () => this.hold(true), { signal });
        this.addEventListener('pointerleave', () => this.hold(false), { signal });
        this.addEventListener('focusin', () => this.hold(true), { signal });
        this.addEventListener('focusout', () => this.hold(false), { signal });
        document.addEventListener('visibilitychange', () => this.sync(), { signal });
        this.motionQuery.addEventListener('change', () => this.sync(), { signal });

        // Off-screen carousels do nothing. The Visit hero is above the fold,
        // but the section is reusable and may not be.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              this.visible = entries.some((entry) => entry.isIntersecting);
              this.sync();
            },
            { threshold: 0.2 }
          );
          this.observer.observe(this);
        } else {
          this.visible = true;
        }

        this.dataset.enhanced = '';
        this.sync();
      }

      disconnectedCallback() {
        this.stop();
        this.observer?.disconnect();
        this.observer = null;
        this.events?.abort();
        this.events = null;
      }

      // Held by the customer (hover or focus) is a different thing from stopped
      // by the page (hidden tab, scrolled away, reduced motion). Keeping them
      // apart means a background tab that regains focus does not start playing
      // under a pointer that is still resting on the card.
      get canPlay() {
        return this.visible !== false && !this.held && !document.hidden && !this.motionQuery.matches;
      }

      hold(held) {
        this.held = held;
        this.sync();
      }

      sync() {
        if (this.canPlay) this.start();
        else this.stop();
      }

      start() {
        if (this.timer || !this.canPlay) return;
        this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
      }

      stop() {
        if (!this.timer) return;
        window.clearInterval(this.timer);
        this.timer = null;
      }

      show(next, { manual = false } = {}) {
        const target = (next + this.slides.length) % this.slides.length;
        if (target === this.index) return;

        this.slides[this.index]?.classList.remove('is-active');
        this.slides[this.index]?.setAttribute('aria-hidden', 'true');
        this.dots[this.index]?.classList.remove('is-active');
        this.dots[this.index]?.removeAttribute('aria-current');

        this.index = target;

        this.slides[target]?.classList.add('is-active');
        this.slides[target]?.removeAttribute('aria-hidden');
        this.dots[target]?.classList.add('is-active');
        this.dots[target]?.setAttribute('aria-current', 'true');

        // A deliberate tap restarts the clock, so the photo the customer chose
        // does not disappear a fraction of a second later.
        if (manual) {
          this.stop();
          this.start();
        }
      }
    }
  );
}
